// TableGridMap.ts
//
// Pure, OfficeJS-free OOXML table topology parser: no Office/Word API, no DOM.
// Reads the physical (as-authored) shape of a `<w:tbl>` - rows, cells,
// `gridSpan`/`vMerge` state, `gridBefore`/`gridAfter` offsets, and the
// declared `tblGrid` column count - out of a flat-OPC document package.

import { XMLParser } from "fast-xml-parser";

/** Vertical merge state declared by `<w:vMerge>` on a physical cell. */
export type VerticalMergeState = "restart" | "continue" | null;

/** A single physical `<w:tc>` cell, before any logical (merge-aware) projection. */
export interface TopologyCell {
  gridSpan: number;
  vMerge: VerticalMergeState;
}

/** A single physical `<w:tr>` row: its cells plus any `gridBefore`/`gridAfter` offsets. */
export interface TopologyRow {
  cells: TopologyCell[];
  gridBefore: number;
  gridAfter: number;
}

/** Raw topology of a `<w:tbl>`: physical rows/cells plus the declared `tblGrid` column count. */
export interface TableTopology {
  logicalColumnCount: number;
  rows: TopologyRow[];
}

/** Address of a physical `<w:tc>`: its `<w:tr>` ordinal and its cell ordinal within that row. */
export interface PhysicalCellRef {
  physicalRow: number;
  physicalCell: number;
}

/** Address of a logical (merge-aware) grid position. */
export interface LogicalPosition {
  row: number;
  column: number;
}

/**
 * A single logical grid position: the physical cell anchored there (`null` when `covered`
 * or unclaimed, e.g. a `gridBefore` gap), the span the anchor owns, and `anchorRow`/
 * `anchorColumn` — the owning anchor's position (self when not `covered`, `null` only when
 * nothing claims this position at all).
 */
export interface LogicalGridPosition {
  physicalRow: number | null;
  physicalCell: number | null;
  colSpan: number;
  rowSpan: number;
  covered: boolean;
  anchorRow: number | null;
  anchorColumn: number | null;
}

/**
 * A rectangular `rowCount` x `columnCount` projection of a `TableTopology` onto logical
 * (merge-aware) coordinates, as built by `buildLogicalGrid`/`buildIdentityGrid`.
 */
export interface LogicalGrid {
  rowCount: number;
  columnCount: number;
  positions: LogicalGridPosition[][];
  /** Logical position -> the physical cell anchored there, or `null` when covered/empty. */
  logicalToPhysical(row: number, column: number): PhysicalCellRef | null;
  /**
   * Physical cell -> the logical position it belongs to. For a `vMerge` continuation this
   * is the anchor's logical position, NOT the continuation's own (covered) position.
   */
  physicalToLogical(row: number, cellOrdinal: number): LogicalPosition | null;
  /**
   * Physical cell -> the logical position that cell itself occupies (row = physical row,
   * column = `gridBefore` + preceding cells' `gridSpan` in that row). Unlike
   * `physicalToLogical`, never redirected to a vertical-merge anchor in an earlier row.
   */
  physicalToOccupiedPosition(row: number, cellOrdinal: number): LogicalPosition | null;
  /**
   * Any logical position -> the position of the anchor that owns it (itself when already
   * an anchor), or `null` when nothing claims that position at all.
   */
  resolveAnchor(row: number, column: number): LogicalPosition | null;
}

const toArray = <T>(v: T | T[] | undefined): T[] => {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
};

const xmlParser = new XMLParser({ ignoreAttributes: false, removeNSPrefix: true });

const parseGridSpan = (tcPr: any): number => {
  const val = tcPr?.gridSpan?.["@_val"];
  return val !== undefined ? Number(val) : 1;
};

// `hasOwnProperty` (not truthiness) is required: a bare `<w:vMerge/>` parses to the falsy "".
const parseVerticalMerge = (tcPr: any): VerticalMergeState => {
  if (tcPr == null || !Object.prototype.hasOwnProperty.call(tcPr, "vMerge")) return null;
  return tcPr.vMerge?.["@_val"] === "restart" ? "restart" : "continue";
};

const parseRowOffset = (trPr: any, attr: "gridBefore" | "gridAfter"): number => {
  const val = trPr?.[attr]?.["@_val"];
  return val !== undefined ? Number(val) : 0;
};

/**
 * Parses the physical row/cell topology of the single `<w:tbl>` found in a flat-OPC
 * document package. Never throws: returns `null` for any malformed input or missing hop
 * (empty/non-XML string, no `/word/document.xml` part, no `<w:tbl>`, no `<w:tblGrid>`,
 * or zero rows).
 */
export function parseTableTopology(ooxml: string): TableTopology | null {
  try {
    const parsed = xmlParser.parse(ooxml);
    const parts = toArray(parsed?.package?.part);
    const documentPart = parts.find((part: any) => part?.["@_name"] === "/word/document.xml");
    const tbl = toArray(documentPart?.xmlData?.document?.body?.tbl)[0];
    if (!tbl) return null;

    const gridCol = tbl.tblGrid?.gridCol;
    if (!gridCol) return null;
    const logicalColumnCount = toArray(gridCol).length;

    const rowsRaw = toArray(tbl.tr);
    if (rowsRaw.length === 0) return null;

    const rows: TopologyRow[] = rowsRaw.map((tr: any) => ({
      cells: toArray(tr?.tc).map((tc: any) => ({
        gridSpan: parseGridSpan(tc?.tcPr),
        vMerge: parseVerticalMerge(tc?.tcPr),
      })),
      gridBefore: parseRowOffset(tr?.trPr, "gridBefore"),
      gridAfter: parseRowOffset(tr?.trPr, "gridAfter"),
    }));

    return { logicalColumnCount, rows };
  } catch {
    return null;
  }
}

const emptyPosition = (): LogicalGridPosition => ({
  physicalRow: null,
  physicalCell: null,
  colSpan: 1,
  rowSpan: 1,
  covered: true,
  anchorRow: null,
  anchorColumn: null,
});

/**
 * Wraps a fully-built `positions`/`physicalToLogicalMap`/`physicalToOccupiedMap` triple into
 * the public `LogicalGrid` shape shared by `buildLogicalGrid` and `buildIdentityGrid`.
 */
const makeLogicalGrid = (
  rowCount: number,
  columnCount: number,
  positions: LogicalGridPosition[][],
  physicalToLogicalMap: (LogicalPosition | null)[][],
  physicalToOccupiedMap: (LogicalPosition | null)[][]
): LogicalGrid => ({
  rowCount,
  columnCount,
  positions,
  logicalToPhysical(row, column) {
    const position = positions[row]?.[column];
    if (!position || position.physicalRow === null || position.physicalCell === null) return null;
    return { physicalRow: position.physicalRow, physicalCell: position.physicalCell };
  },
  physicalToLogical(row, cellOrdinal) {
    return physicalToLogicalMap[row]?.[cellOrdinal] ?? null;
  },
  physicalToOccupiedPosition(row, cellOrdinal) {
    return physicalToOccupiedMap[row]?.[cellOrdinal] ?? null;
  },
  resolveAnchor(row, column) {
    const position = positions[row]?.[column];
    if (!position || position.anchorRow === null || position.anchorColumn === null) return null;
    return { row: position.anchorRow, column: position.anchorColumn };
  },
});

/**
 * Projects a parsed `TableTopology` onto a rectangular, merge-aware `LogicalGrid` via an
 * occupancy walk: each physical row starts at logical column `gridBefore`, each physical
 * cell claims `gridSpan` logical columns (marking the trailing ones `covered`), and a
 * `vMerge: "continue"` cell is attributed back to its `"restart"` anchor, extending the
 * anchor's `rowSpan` instead of claiming a logical position of its own.
 *
 * Returns `null` ONLY on positive disagreement between the topology and `physicalRows`
 * (the per-row physical cell counts Office reports): a row-count mismatch, a row's
 * physical cell count mismatch, or a row whose `gridBefore + sum(gridSpan) + gridAfter`
 * does not equal `logicalColumnCount`. An unusual but internally consistent table (e.g. a
 * full-width merged header) is NOT a disagreement and must not return `null`.
 */
export function buildLogicalGrid(topology: TableTopology, physicalRows: number[]): LogicalGrid | null {
  if (topology.rows.length !== physicalRows.length) return null;

  for (let r = 0; r < topology.rows.length; r++) {
    const row = topology.rows[r];
    if (row.cells.length !== physicalRows[r]) return null;

    const spanSum = row.cells.reduce((sum, cellDef) => sum + cellDef.gridSpan, 0);
    if (row.gridBefore + spanSum + row.gridAfter !== topology.logicalColumnCount) return null;
  }

  const rowCount = topology.rows.length;
  const columnCount = topology.logicalColumnCount;
  const positions: LogicalGridPosition[][] = Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, emptyPosition)
  );
  const physicalToLogicalMap: (LogicalPosition | null)[][] = topology.rows.map((row) =>
    new Array(row.cells.length).fill(null)
  );
  const physicalToOccupiedMap: (LogicalPosition | null)[][] = topology.rows.map((row) =>
    new Array(row.cells.length).fill(null)
  );

  // Logical column, per column, that still owns an open vertical-merge chain (i.e. the
  // most recent `"restart"` anchor at that column, extended by contiguous `"continue"`s).
  const verticalAnchors = new Map<number, LogicalPosition>();

  topology.rows.forEach((row, r) => {
    let column = row.gridBefore;

    row.cells.forEach((cellDef, i) => {
      const span = cellDef.gridSpan;
      physicalToOccupiedMap[r][i] = { row: r, column };
      const continuation = cellDef.vMerge === "continue" ? verticalAnchors.get(column) : undefined;
      const owningAnchor = continuation ?? { row: r, column };

      if (continuation) {
        // Covered continuation: attributed to the anchor, which gains one more rowSpan.
        positions[continuation.row][continuation.column].rowSpan += 1;
        positions[r][column] = { ...emptyPosition(), anchorRow: continuation.row, anchorColumn: continuation.column };
        physicalToLogicalMap[r][i] = continuation;
      } else {
        // A new logical anchor: a plain cell, a `"restart"`, or a malformed orphan
        // `"continue"` with no open chain (treated defensively as its own anchor).
        positions[r][column] = {
          physicalRow: r,
          physicalCell: i,
          colSpan: span,
          rowSpan: 1,
          covered: false,
          anchorRow: r,
          anchorColumn: column,
        };
        physicalToLogicalMap[r][i] = { row: r, column };
        if (cellDef.vMerge === null) {
          verticalAnchors.delete(column);
        } else {
          verticalAnchors.set(column, { row: r, column });
        }
      }

      for (let k = column + 1; k < column + span; k++) {
        positions[r][k] = { ...emptyPosition(), anchorRow: owningAnchor.row, anchorColumn: owningAnchor.column };
      }
      column += span;
    });
  });

  return makeLogicalGrid(rowCount, columnCount, positions, physicalToLogicalMap, physicalToOccupiedMap);
}

/**
 * STRICT merge-free check: the topology must have parsed, carry no `gridSpan > 1`, no
 * `vMerge` of any kind, no `gridBefore`/`gridAfter`, and every row's physical width must
 * equal `logicalColumnCount`. A physically-rectangular table that only uses vertical
 * merges (which preserve physical cell count per row) is NOT merge-free.
 */
export function isMergeFree(topology: TableTopology | null): boolean {
  if (!topology) return false;

  return topology.rows.every((row) => {
    if (row.gridBefore !== 0 || row.gridAfter !== 0) return false;
    if (row.cells.length !== topology.logicalColumnCount) return false;
    return row.cells.every((cellDef) => cellDef.gridSpan === 1 && cellDef.vMerge === null);
  });
}

/**
 * Builds a trivial identity `LogicalGrid` (logical == physical) directly from Office's
 * per-row physical cell counts, with no merge awareness at all. This is the fallback for
 * when OOXML is simply unavailable - see Known Limitation A3. Returns `null` unless every
 * entry of `physicalRows` is equal (a uniform, physically rectangular grid), and unless
 * there is at least one row.
 */
export function buildIdentityGrid(physicalRows: number[]): LogicalGrid | null {
  if (physicalRows.length === 0) return null;
  const columnCount = physicalRows[0];
  if (!physicalRows.every((width) => width === columnCount)) return null;

  const rowCount = physicalRows.length;
  const positions: LogicalGridPosition[][] = Array.from({ length: rowCount }, (_, r) =>
    Array.from({ length: columnCount }, (_, c) => ({
      physicalRow: r,
      physicalCell: c,
      colSpan: 1,
      rowSpan: 1,
      covered: false,
      anchorRow: r,
      anchorColumn: c,
    }))
  );
  const physicalToLogicalMap: (LogicalPosition | null)[][] = Array.from({ length: rowCount }, (_, r) =>
    Array.from({ length: columnCount }, (_, c) => ({ row: r, column: c }))
  );

  // No merges at all in an identity grid: every physical cell occupies exactly its own
  // (identity) logical position, so the occupied-position map is the same map instance.
  return makeLogicalGrid(rowCount, columnCount, positions, physicalToLogicalMap, physicalToLogicalMap);
}
